import { X, Trash2, Tag, UserPlus, ArrowRightLeft, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"

export interface BulkAction {
  id: string
  label: string
  icon: React.ReactNode
  variant?: "default" | "destructive"
  onClick: () => void
}

interface BulkActionBarProps {
  selectedCount: number
  onDeselectAll: () => void
  actions: BulkAction[]
}

export function BulkActionBar({ selectedCount, onDeselectAll, actions }: BulkActionBarProps) {
  if (selectedCount === 0) return null

  return (
    <div
      className="flex items-center gap-3 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 mb-2 animate-in fade-in slide-in-from-top-2 duration-200"
    >
      <span className="text-sm font-medium whitespace-nowrap">
        Wybrano {selectedCount} {selectedCount === 1 ? "element" : selectedCount < 5 ? "elementy" : "elementow"}
      </span>

      <div className="h-4 w-px bg-white/30" />

      <div className="flex items-center gap-1.5 flex-wrap">
        {actions.map((action) => (
          <Button
            key={action.id}
            variant="ghost"
            size="sm"
            className={`h-7 gap-1.5 text-xs font-medium ${
              action.variant === "destructive"
                ? "text-red-200 hover:text-red-100 hover:bg-red-500/30"
                : "text-primary-foreground/90 hover:text-primary-foreground hover:bg-white/15"
            }`}
            onClick={action.onClick}
          >
            {action.icon}
            {action.label}
          </Button>
        ))}
      </div>

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-xs text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/15"
        onClick={onDeselectAll}
      >
        <X size={14} />
        Odznacz wszystko
      </Button>
    </div>
  )
}

// Helper to build common action configs
export function buildDeleteAction(onClick: () => void): BulkAction {
  return {
    id: "delete",
    label: "Usun",
    icon: <Trash2 size={14} />,
    variant: "destructive",
    onClick,
  }
}

export function buildTagAction(onClick: () => void): BulkAction {
  return {
    id: "tag",
    label: "Dodaj tagi",
    icon: <Tag size={14} />,
    onClick,
  }
}

export function buildAssignAction(onClick: () => void, label = "Przypisz"): BulkAction {
  return {
    id: "assign",
    label,
    icon: <UserPlus size={14} />,
    onClick,
  }
}

export function buildStatusAction(onClick: () => void, label = "Zmien status"): BulkAction {
  return {
    id: "status",
    label,
    icon: <ArrowRightLeft size={14} />,
    onClick,
  }
}

export function buildPriorityAction(onClick: () => void): BulkAction {
  return {
    id: "priority",
    label: "Zmien priorytet",
    icon: <AlertTriangle size={14} />,
    onClick,
  }
}
